import { DEFAULT_EXCLUSIONS, PERSONA_AUDIENCES, resolveByName } from './audiences';
import type { LinkedInDraft, MetaDraft } from './draft-fixtures';
import { formName, growthFormDefaults } from './lead-forms';
import { adSetName, campaignName } from './naming';
import {
  MetaLeadFormSpecSchema,
  NO_OVERRIDES,
  type MetaLeadFormSpec,
  type AdRequestDetail,
  type MetaAudience,
  type MetaAudienceRef,
  type Persona,
  type AdRequestSubmit,
  type CreativeOverrides,
  type CreativeSpec,
  type LinkedInRequestSubmit,
  type RequestCreative,
} from './types';

// A creative's media. For a video, `file`/`previewUrl` are its thumbnail frame and `video` the
// video itself. `file` (and `video.file`) are null for media already saved on the request; only a
// newly picked file uploads. Dimensions are known only for a newly picked file.
export interface MetaImage {
  file: File | null;
  previewUrl: string;
  width?: number;
  height?: number;
  video?: { file: File | null; url: string | null; id: string | null };
}

/** One ad of the request. `id` is set once it's saved; `key` is stable for React either way. */
export interface DraftCreative {
  key: string;
  id: string | null;
  name: string;
  /** The main asset, shown in feeds (4:5 or 1:1). */
  media: MetaImage | null;
  /** The 9:16 version for Stories and Reels, the same kind of media as `media`. */
  vertical: MetaImage | null;
  /** Null fields use the request's shared copy. */
  overrides: CreativeOverrides;
}

export function newCreative(existing: DraftCreative[], fields: Partial<DraftCreative> = {}): DraftCreative {
  const names = new Set(existing.map(c => c.name));
  let n = existing.length + 1;
  while (names.has(`ad${n}`)) n++;
  return { key: crypto.randomUUID(), id: null, name: `ad${n}`, media: null, vertical: null, overrides: NO_OVERRIDES, ...fields };
}

export const hasOverrides = (c: { overrides: CreativeOverrides }) =>
  c.overrides.primaryText !== null || c.overrides.headline !== null || c.overrides.description !== null;

/** The copy a creative's ad runs with: its overrides where set, the shared copy otherwise. */
export function creativeCopy(draft: Pick<MetaDraft, 'primaryText' | 'headline' | 'description'>, overrides: CreativeOverrides) {
  return {
    primaryText: overrides.primaryText ?? draft.primaryText,
    headline: overrides.headline ?? draft.headline,
    description: overrides.description ?? draft.description,
  };
}

/** An imported ad decides the destination: its Instant Form, or its website when it has one. */
export function withImportedDestination(draft: MetaDraft, imported: { destinationUrl: string; leadFormId: string | null }): MetaDraft {
  if (imported.leadFormId) {
    // The imported ad's form is used as it is until someone duplicates it to edit.
    return { ...draft, destination: 'lead_form', leadFormId: imported.leadFormId, leadFormName: null, leadForm: null };
  }
  if (imported.destinationUrl) {
    return { ...draft, destination: 'website', destinationUrl: imported.destinationUrl, leadFormId: null, leadFormName: null };
  }
  return draft;
}

/** What still blocks submitting the draft, in words for the header ("Needs …"). */
export function missingFields(draft: MetaDraft, creatives: DraftCreative[]): string[] {
  const missing: string[] = [];
  if (!draft.name.trim()) missing.push('campaign name');
  if (!draft.primaryText.trim()) missing.push('primary text');
  if (!draft.headline.trim()) missing.push('headline');
  if (draft.destination === 'lead_form') {
    if (!draft.leadForm && !draft.leadFormId) missing.push('an Instant Form');
    if (draft.leadForm && !MetaLeadFormSpecSchema.safeParse(draft.leadForm.spec).success) missing.push('a complete Instant Form');
  } else if (!draft.destinationUrl.startsWith('https://')) {
    missing.push('an https destination URL');
  }
  if (!(Number(draft.dailyBudgetUsd) >= 1)) missing.push('a daily budget of at least $1');
  if (!draft.startDate) missing.push('start date');
  if (draft.endDate && draft.endDate <= draft.startDate) missing.push('an end date after the start date');
  if (creatives.length === 0) missing.push('a creative');
  if (creatives.some(c => !c.name.trim())) missing.push('a name for every creative');
  const noMedia = creatives.filter(c => !c.media);
  if (noMedia.length > 0) {
    missing.push(creatives.length === 1 ? 'an image' : `an image or video for ${noMedia.map(c => c.name || 'each creative').join(', ')}`);
  }
  const unexplained = draft.droppedExclusions.filter(d => d.reason.trim().length < 3);
  if (unexplained.length > 0) missing.push(`why you dropped ${unexplained.map(d => d.name).join(', ')} from the exclusions`);
  return missing;
}

const overrideOrNull = (value: string | null) => (value?.trim() ? value.trim() : null);

/** Creatives with main media but no 9:16 version; Meta recommends one for Stories and Reels. */
export function missingVertical(creatives: DraftCreative[]): DraftCreative[] {
  return creatives.filter(c => c.media && !c.vertical);
}

/** The video ids are the creative's saved videos, or the ones just uploaded for it. */
export function toCreativeSpec(
  creative: DraftCreative,
  videoId: string | null,
  verticalVideoId: string | null,
): CreativeSpec {
  const video = videoId !== null;
  // A vertical version of the other kind (left from before the main media changed) is dropped.
  const vertical = creative.vertical !== null && (verticalVideoId !== null) === video;
  return {
    ...(creative.id && { id: creative.id }),
    name: creative.name.trim(),
    creativeType: video ? 'video' : 'image',
    videoId,
    vertical,
    verticalVideoId: vertical ? verticalVideoId : null,
    overrides: {
      primaryText: overrideOrNull(creative.overrides.primaryText),
      headline: overrideOrNull(creative.overrides.headline),
      description: overrideOrNull(creative.overrides.description),
    },
  };
}

/** Fills the campaign and ad set names from the program and persona, until they're edited by hand. */
export function withNaming(draft: MetaDraft): MetaDraft {
  if (!draft.program.trim()) return draft;
  const objective = draft.destination === 'lead_form' ? 'leads' : 'traffic';
  const names = draft.namesEdited
    ? {}
    : { name: campaignName(draft.program, objective), adSetName: adSetName(draft.program, objective, draft.personas) };
  const form = draft.leadForm;
  const leadForm = form && !form.nameEdited ? { ...form, spec: { ...form.spec, name: formName(draft.program) } } : form;
  return { ...draft, ...names, leadForm };
}

/** Starts the form over from Growth's standard one, or from a duplicated form's content. */
export function withLeadForm(draft: MetaDraft, spec: MetaLeadFormSpec, sourceId: string | null): MetaDraft {
  return withNaming({ ...draft, leadForm: { spec, sourceId, nameEdited: false }, leadFormId: null, leadFormName: null });
}

/** The audiences of these personas that the account has, in persona order, each once. */
export function personaTargets(personas: readonly Persona[], audiences: MetaAudience[]): MetaAudienceRef[] {
  const found = personas.flatMap(p => resolveByName(audiences, PERSONA_AUDIENCES[p]).found);
  return [...new Map(found.map(a => [a.id, a])).values()];
}

/**
 * Picks personas; their audiences become the targets, unless the targets were picked by hand. Targets
 * that are just the previous personas' audiences (set by personas, the agent or an import) are replaced.
 */
export function withPersonas(draft: MetaDraft, personas: Persona[], audiences: MetaAudience[]): MetaDraft {
  const previous = new Set(personaTargets(draft.personas, audiences).map(a => a.id));
  const onlyPrevious = draft.targets.length === previous.size && draft.targets.every(t => previous.has(t.id));
  const replace = !draft.targetsEdited || draft.targets.length === 0 || onlyPrevious;
  return withNaming({ ...draft, personas, targets: replace ? personaTargets(personas, audiences) : draft.targets });
}

/** The exclusions to save: the ones picked, or Growth's defaults that exist in the account. */
export function resolvedExclusions(draft: MetaDraft, audiences: MetaAudience[]): MetaAudienceRef[] {
  return draft.exclusions ?? resolveByName(audiences, DEFAULT_EXCLUSIONS).found;
}

/** Pass the account's audiences when the draft may still hold Growth's default exclusions (`exclusions: null`). */
export function toSubmit(draft: MetaDraft, creatives: CreativeSpec[], audiences: MetaAudience[] = []): AdRequestSubmit {
  const lead = draft.destination === 'lead_form';
  return {
    creatives,
    name: draft.name.trim(),
    program: draft.program.trim(),
    personas: draft.personas,
    adSetName: draft.adSetName.trim(),
    targeting: {
      included: draft.targets,
      excluded: resolvedExclusions(draft, audiences),
      droppedExclusions: draft.droppedExclusions.map(d => ({ name: d.name, reason: d.reason.trim() })),
    },
    team: draft.team.trim(),
    primaryText: draft.primaryText.trim(),
    headline: draft.headline.trim(),
    description: draft.description.trim(),
    callToAction: draft.callToAction,
    destinationUrl: lead ? '' : draft.destinationUrl.trim(),
    leadFormSpec: lead && draft.leadForm ? draft.leadForm.spec : null,
    leadFormSourceId: lead && draft.leadForm ? draft.leadForm.sourceId : null,
    leadFormId: lead && !draft.leadForm ? draft.leadFormId : null,
    leadFormName: lead && !draft.leadForm ? draft.leadFormName : null,
    growthSettings: draft.growthSettings,
    dailyBudgetUsd: Number(draft.dailyBudgetUsd),
    startDate: draft.startDate,
    endDate: draft.endDate || null,
  };
}

export function fromRequest(request: AdRequestDetail): MetaDraft {
  return {
    name: request.name,
    team: request.team,
    primaryText: request.primaryText,
    headline: request.headline,
    description: request.description,
    callToAction: request.callToAction,
    destinationUrl: request.destinationUrl,
    destination: request.leadFormId || request.leadFormSpec ? 'lead_form' : 'website',
    growthSettings: request.growthSettings,
    leadFormId: request.leadFormId,
    leadFormName: request.leadFormName,
    leadForm: request.leadFormSpec
      ? { spec: request.leadFormSpec, sourceId: request.leadFormSourceId, nameEdited: true }
      : request.leadFormId
        ? null
        : { spec: growthFormDefaults(request.program), sourceId: null, nameEdited: false },
    program: request.program,
    personas: request.personas,
    adSetName: request.adSetName,
    // A saved request keeps its names as they are.
    namesEdited: true,
    targets: request.targeting.included,
    targetsEdited: true,
    // A request saved before exclusions existed has none; it starts from Growth's defaults like a new one.
    exclusions: request.targeting.excluded.length > 0 || request.targeting.droppedExclusions.length > 0 ? request.targeting.excluded : null,
    droppedExclusions: request.targeting.droppedExclusions,
    dailyBudgetUsd: String(request.dailyBudgetUsd),
    startDate: request.startDate,
    endDate: request.endDate ?? '',
  };
}

const savedMedia = (previewUrl: string | undefined, videoId: string | null): MetaImage | null =>
  previewUrl ? { file: null, previewUrl, video: videoId ? { file: null, url: null, id: videoId } : undefined } : null;

/** The maps hold each creative's loaded images, keyed by creative id. */
export function fromRequestCreatives(
  creatives: RequestCreative[],
  images: { feed: Map<string, string>; vertical: Map<string, string> },
): DraftCreative[] {
  return creatives.map(c => ({
    key: c.id,
    id: c.id,
    name: c.name,
    media: savedMedia(images.feed.get(c.id), c.videoId),
    vertical: savedMedia(images.vertical.get(c.id), c.verticalVideoId),
    overrides: c.overrides,
  }));
}

export function missingLinkedInFields(draft: LinkedInDraft): string[] {
  const missing: string[] = [];
  if (!draft.name.trim()) missing.push('campaign name');
  if (!draft.postUrl.trim().startsWith('https://')) missing.push('a post link');
  if (!draft.campaignGroupId) missing.push('a campaign group');
  if (!draft.sourceCampaignId) missing.push('a campaign to copy');
  if (!(Number(draft.dailyBudgetUsd) >= 10)) missing.push('a daily budget of at least $10');
  if (!draft.startDate) missing.push('start date');
  if (draft.endDate && draft.endDate <= draft.startDate) missing.push('an end date after the start date');
  return missing;
}

/** Call only when missingLinkedInFields is empty. */
export function toLinkedInSubmit(draft: LinkedInDraft, postUrn: string | null): LinkedInRequestSubmit {
  return {
    platform: 'linkedin',
    name: draft.name.trim(),
    team: draft.team.trim(),
    postUrl: draft.postUrl.trim(),
    postUrn,
    campaignGroupId: draft.campaignGroupId ?? '',
    campaignGroupName: draft.campaignGroupName,
    sourceCampaignId: draft.sourceCampaignId ?? '',
    sourceCampaignName: draft.sourceCampaignName,
    dailyBudgetUsd: Number(draft.dailyBudgetUsd),
    startDate: draft.startDate,
    endDate: draft.endDate || null,
  };
}
